<template>
  <v-col>
    <v-card class="elevation-0">
      <v-card-title>
        <v-col>
          <v-row>
            <v-col class="col-auto pr-0">
              <h3 class="font-weight-bold">
                Logs
              </h3>
            </v-col>

            <!-- Refresh Logs -->
            <v-col class="col-auto pl-1">
              <v-btn
                color="primary"
                dense
                small
                icon
                @click="fetchLogs"
              >
                <font-awesome-icon
                  :icon="['fas', 'sync']"
                  size="lg"
                />
              </v-btn>
            </v-col>
          </v-row>

          <p class="caption">
            <span v-if="hasAccessToLogs">View system logs generated by your organization(s).</span>
            <span v-else>View system logs generated by your actions.</span>
          </p>
        </v-col>

        <v-spacer />

        <v-card-actions>
          <!-- <v-btn
            color="primary"
            @click="normalizeLogs"
          >
            Normalize Logs
          </v-btn> -->

          <v-col>
            <v-row class="align-center">
              <!-- Filter Logs -->
              <v-col class="pt-0">
                <v-select
                  v-model="filterBy"
                  class="mr-4"
                  :items="filterOptions"
                  item-text="text"
                  item-value="value"
                  label="Filter"
                  hide-details
                  @change="fetchLogs"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-data-table
          class="elevation-0"
          :headers="logHeaders"
          :items="logs"
          :items-per-page="50"
          :items-per-page-options="[10, 50, 100, 500]"
          :loading="isLoading"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              @click="viewLogDetails(item)"
            >
              <v-icon>visibility</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.changes="{ item }">
            <td
              v-if="item.changes && item.changes.length > 0"
              class="caption font-weight-medium"
            >
              <span
                v-for="(change, index) in item.changes"
                :key="index"
              >
                {{ change.message }}<span v-if="index < item.changes.length - 1">, </span>
              </span>
            </td>

            <td
              v-else
              class="caption font-weight-medium"
            >
              No changes
            </td>
          </template>

          <template v-slot:no-data>
            <div class="text-center">
              {{ errorMessage || 'No logs found.' }}
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="showLogDetails"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          <v-col>
            <v-row>
              <v-col class="col-auto">
                <span class="headline">Log Details</span>
              </v-col>

              <v-spacer />

              <v-col class="col-auto">
                <v-btn
                  icon
                  @click="closeLogDetails"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-card-title>

        <v-card-text>
          <v-col>
            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Log ID</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <span class="caption font-weight-medium">{{ selectedLog.docId }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Timestamp</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <span class="caption font-weight-medium">{{ selectedLog.commitTimestamp }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Type</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <span class="caption font-weight-medium">{{ selectedLog.logType }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Actor</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <span class="caption font-weight-medium">{{ selectedLog.committerEmail }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Action</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <span class="caption font-weight-medium">{{ selectedLog.logEvent }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Object</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <span class="caption font-weight-medium">{{ selectedLog.commitDocumentId }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row>
                  <v-col class="col-auto">
                    <span class="caption font-weight-bold">Changes</span>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col class="pt-0">
                    <v-row
                      v-for="change in selectedLog.changes"
                      :key="change.index"
                      class="caption font-weight-medium"
                    >
                      {{ change.message }}
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'AdminLog',
  data() {
    return {
      errorMessage: '',

      filterFunctions: {
        all: (log) => log,
        access: (log) => log.logType === 'access',
      },
      filterName: 'all',
      filterOptions: [
        {
          text: 'All',
          value: 'all',
        },
        {
          text: 'Access',
          value: 'access',
        },
        {
          text: 'Activity',
          value: 'activity',
        },
      ],

      isLoading: false,

      logs: [],
      logActions: [
        {
          text: 'View Log',
          value: 'viewLog',
          icon: 'visibility',
          event: (log) => {
            console.log('Viewing log details:', log)

            this.viewLogDetails(log)
          },
        },
      ],

      search: '',

      selectedLog: {},
      showLogDetails: false,
    }
  },
  computed: {
    ...mapAuthGetters([
      'isUserInternalAdmin',
      'hasAccessToLogs',
    ]),

    filterBy: {
      get() {
        return this.filterName || 'all'
      },
      set(selectedValue) {
        this.filterName = selectedValue
      },
    },
    logHeaders() {
      const headers = [
        {
          sortable: true,
          text: 'Timestamp',
          value: 'logTimestamp',
        },
        {
          sortable: true,
          text: 'Type',
          value: 'logType',
          visible: false,
        },
        {
          sortable: true,
          text: 'Actor',
          value: 'committerEmail',
        },
        {
          sortable: true,
          text: 'Action',
          value: 'logEvent',
        },
        {
          sortable: true,
          text: 'Object',
          value: 'commitDocumentId',
        },
        // {
        //   sortable: true,
        //   text: 'Changes',
        //   value: 'changedProperties',
        //   // formatter: (changes) => changes.join(', '),
        // },
        {
          sortable: true,
          text: 'View',
          value: 'actions',
          align: 'end',
        },
      ]

      return headers
    },
  },
  created() {
    this.fetchLogs()
  },
  methods: {
    catchLogs(logError) {
      this.isLoading = false

      const { error } = logError || {}
      const haveFormattedError = error && typeof error === 'object' && error.message

      this.errorMessage = haveFormattedError ? error.message : 'Failed to fetch logs.'

      return logError
    },
    async fetchLogs() {
      this.isLoading = true

      const logs = await this.$store.dispatch('admin/fetchLogs')

      if (!logs || logs.error) {
        return this.catchLogs(logs)
      }

      this.errorMessage = ''

      if (this.filterBy && this.filterBy !== 'all') {
        // Get the filter function based on the selected filter
        const filterFunction = this.filterFunctions[this.filterBy]

        // Apply the filters to the logs
        this.logs = this.logs.filter((log) => filterFunction(log))
      } else {
        this.logs = logs
      }

      this.isLoading = false

      return logs
    },

    closeLogDetails() {
      this.showLogDetails = false
      this.selectedLog = {}
    },

    viewLogDetails(log) {
      this.selectedLog = log
      this.showLogDetails = true
    },

    normalizeLogs() {
      this.$store.dispatch('admin/normalizeLogs')
    },
  },
}
</script>
